import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { useSetAddToCalendarConfig } from '@/features/experiences/hooks/useSetAddToCalendarConfig';
import { getExperienceAddToCalendarAccess } from '@/features/interactions/addToCalendar/access.lib';
import { isExperienceAddToCalendarInherited } from '@/features/interactions/lib/inheritance.lib';
import { AddToCalendarConfigAccess, Role } from '@/types/gql.generated';
import { Box, Text } from '@/ui';
import { SidebarCard, SidebarRow, SidebarRowLabel } from '../../SidebarKit';
import { SidebarRowSelect } from '../../SidebarRowSelect';
import { SidebarRowSwitch } from '../../SidebarRowSwitch';
import { SectionHeader } from '../SectionHeader';

const ACCESS_OPTIONS = [
  { id: AddToCalendarConfigAccess.Off, label: 'No one' },
  { id: AddToCalendarConfigAccess.Owner, label: 'Just me' },
  { id: AddToCalendarConfigAccess.Collaborator, label: 'Collaborators' },
  { id: AddToCalendarConfigAccess.Public, label: 'Public visitors' },
];

const ACCESS_DESCRIPTIONS: Record<AddToCalendarConfigAccess, string> = {
  [AddToCalendarConfigAccess.Off]: 'Calendar tools are off for this schedule.',
  [AddToCalendarConfigAccess.Owner]:
    'Only you can add events from this schedule to your own calendar.',
  [AddToCalendarConfigAccess.Collaborator]:
    'You and everyone you have shared this schedule with can add events to their calendars.',
  [AddToCalendarConfigAccess.Public]:
    'Anyone who can see this schedule can add events to their calendar.',
};

// Like ACCESS_DESCRIPTIONS above, the row describes what the schedule does now
// rather than what the switch would do next, so each state gets its own line.
const IN_PAGE_ON_DESCRIPTION =
  'Displays controls directly in supported views. Controls are always available via the Send to calendar button.';
const IN_PAGE_OFF_DESCRIPTION =
  'Controls are only available via the Send to calendar button.';

export const AddToCalendarSection = () => {
  const { experience } = usePageExperienceContext();
  const { setAddToCalendarAccess, setAddToCalendarInherited } =
    useSetAddToCalendarConfig();

  // Derived from the experience rather than held in state — optimistic updates
  // from the mutation keep it in sync, and a cache refetch is reflected too.
  // An app with nothing stored is unrestricted, which is what PUBLIC means.
  const access =
    getExperienceAddToCalendarAccess(experience) ??
    AddToCalendarConfigAccess.Public;

  // Whether the interaction cascades onto the child views that render the pages,
  // which is what puts a control on each event card. Read from the experience for
  // the same reason as `access` above.
  const isInherited = isExperienceAddToCalendarInherited(experience);

  // Who a schedule is shared with is the owner's decision, and the server
  // refuses this setting from anyone else. A collaborator still sees the value,
  // read-only — the same split the share modal's public-link switch uses.
  const isOwner = experience.role === Role.Owner;

  return (
    <Box maxW="700px">
      <SectionHeader
        description="Choose who can add this schedule to their own calendars and where the controls are shown."
        title="Send to calendar"
      />

      <SidebarCard overflow="visible">
        <SidebarRow align="flex-start" py={3}>
          <SidebarRowLabel
            label="Who can add to their calendar"
            sublabel={ACCESS_DESCRIPTIONS[access]}
          />
          <SidebarRowSelect
            isDisabled={!isOwner}
            minW="170px"
            options={ACCESS_OPTIONS}
            value={access}
            onChange={setAddToCalendarAccess}
          />
        </SidebarRow>

        <SidebarRow align="flex-start" py={3}>
          <SidebarRowLabel
            label="Include controls in page"
            sublabel={
              isInherited ? IN_PAGE_ON_DESCRIPTION : IN_PAGE_OFF_DESCRIPTION
            }
          />
          <SidebarRowSwitch
            aria-label="Include controls in page"
            isChecked={isInherited}
            onChange={() => setAddToCalendarInherited(!isInherited)}
          />
        </SidebarRow>
      </SidebarCard>

      {!isOwner && (
        <Text color="ink.muted" fontSize="ah-status" mt={2}>
          Only the owner can change who can add to their calendar.
        </Text>
      )}
    </Box>
  );
};
